import { NumberFilter, OptionFilter, StringFilter } from '@common/searchFilter/searchFilter.models';
import { IFileInfo } from '@file/file.models';
import {
  IProjectDesignModificationReasonOfChange,
  IReasonOfChangeBase,
  IUpsertProjectDesignModificationReasonOfChangeRequest,
} from 'src/reasonOfChange/reasonOfChange.models';
import {
  IProjectDrawingRegisterSearch,
  IProjectDrawingRegisterWithHistory,
  IUpsertProjectDrawingRegisterRequest,
} from '@drawing/projectDrawingRegister.models';
import { IMasterScheduleOption, IProjectMasterScheduleBase } from '@masterSchedule/masterSchedule.models';
import { IProjectTeam, IProjectTeamOption } from '@projectTeam/projectTeam.models';
import { IProjectFileBase, IProjectFileFormCreateRequest } from '@file/projectFile/projectFile.models';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import { IProject } from '@project/project.models';
import { IApiBaseProps } from '@common/api.models';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { IOption } from '@gcl/react-core';
import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { IProjectTransmittalBase } from '@transmittal/transmittal.models';

export interface IProjectDesignModificationWithLink extends IProjectDesignModificationBase {
  projectTransmittalDesignModificationRowVersion: string;
  projectTransmittalDesignModificationLinkId: number;
}
export interface IProjectDesignModificationBase {
  id: number;
  projectId: number;
  projectMasterScheduleId: number;
  designChangeNo: string;
  location: string;
  elementOfWorks: string;
  changeDetail: string;
  nominatedCheckerProjectTeamId: number;
  siteEngineerProjectUserProfileId?: number;
  siteEngineerDate?: string;
  twcProjectUserProfileId?: number;
  twcDate?: string;
  designerApproval?: boolean;
  iceApproval?: boolean;
  designerProjectUserProfileId?: number;
  designerDate?: string;
  designerComment?: string;
  designerStatus?: string;
  twcIssueProjectUserProfileId?: number;
  twcIssueDate?: string;
  iceProjectUserProfileId?: number;
  iceDate?: string;
  iceComment?: string;
  iceStatus?: string;
  reviewerProjectUserProfileId?: number;
  reviewerProjectUserProfile?: IProjectUserProfile;
  reviewerDate?: string;
  reviewerComment?: string;
  reviewerStatus?: string;
  isFinalize?: boolean;
  status?: string;
  isMajorChange?: boolean;
  isDesignClosed?: boolean;
  isIceAction?: boolean;
  isTwcAction?: boolean;
  isIssuedToIce?: boolean;
  isIceUploaded?: boolean;
  riskCat?: string;
  afterClosedStatus?: string;
  targetDate?: string;
  remark?: string;
  rfcProjectUserProfileId?: number;
  rfcOn?: string;
  rfcProjectUserProfile?: IProjectUserProfile;
  isDesignerAdministratorInitiate?: boolean;
  requiredApprovalDate?: string;
  cancelledName?: string;
  cancelledDate?: string;
  originalDesignModificationId?: number;
  createdDateTime: string;
  createdBy: number;
  lastModifiedDateTime?: string;
  lastModifiedBy: number;
  rowVersion?: string;
}
export interface IProjectDesignModification extends IProjectDesignModificationBase {
  siteEngineerProjectUserProfile: IProjectUserProfile;
  designerProjectUserProfile: IProjectUserProfile;
  twcprojectUserProfile: IProjectUserProfile;
  twcissueProjectUserProfile: IProjectUserProfile;
  iceprojectUserProfile: IProjectUserProfile;
  reviewerProjectUserProfile: IProjectUserProfile;
  project: IProject;
  masterSchedule: IProjectMasterScheduleBase;
  proposedModificationAttachments?: IFileInfo[];
  changeReasons?: IProjectDesignModificationReasonOfChange[];
  nominatedCheckerCompany: IProjectCompanyOption;
  nominatedChecker: IProjectTeam;
  nominatedDesignerCompany: IProjectCompanyOption;
  nominatedDesigner: IProjectTeam;
  drawingsModified?: IProjectDesignModificationDrawing[];
  designerAttachments?: IFileInfo[];
  relatedDesignerTransmittal?: IProjectTransmittalBase;
  riskCategory?: string;
  createdDate: string;
}

export interface IProjectDesignModificationDrawingBase {
  id: number;
  projectDesignModificationId: number;
  projectDrawingRegisterId: number;
  projectDrawingRegisterHistoryId: number;
  status: string;
  createdDateTime: string;
  createdBy: number;
  lastModifiedDateTime: string;
  lastModifiedBy: number;
  rowVersion?: string;
}

export interface IProjectDesignModificationDrawing extends IProjectDesignModificationDrawingBase {
  drawingRegister: IProjectDrawingRegisterWithHistory;
}

export interface IUpsertProjectDesignModificationDrawingRequest {
  id?: number;
  projectDesignModificationId?: number;
  projectDrawingRegisterId: number;
  projectDrawingRegisterHistoryId: number;
  rowVersion?: string;
}

export interface ICreateProjectDesignModificationRequest {
  projectId: number;
  masterScheduleId: number;
  designChangeNo?: string;
  location?: string;
  elementOfWorks?: string;
  changeDetail?: string;
  changeReasons?: IUpsertProjectDesignModificationReasonOfChangeRequest[];
  riskCategory: string;
  nominatedChecker: IProjectTeam;
  nominatedCheckerCompany: IProjectCompanyOption;
  nominatedDesigner: IProjectTeam;
  nominatedDesignerCompany: IProjectCompanyOption;
  requiredApprovalDate: string;
  drawingsModified?: IUpsertProjectDrawingRegisterRequest[];
  designerComment?: string;
  iceComment?: string;
  discissions?: IUpsertProjectDesignModificationDiscussionRequest[];
  proposedModificationAttachments?: IProjectFileFormCreateRequest[];
  designerAttachments?: IProjectFileFormCreateRequest[];
}

export interface IUpsertProjectDesignModificationDiscussionRequest {
  id?: number;
  projectId: number;
  projectDesignModificationId: number;
  discussionDate: string;
  comment?: string;
  furtherAction?: string;
  repliedDate?: string;
  isDesignAdministrator?: boolean;
  type?: string;
}

export interface IUpdateProjectDesignModificationRequest {
  projectId: number;
  masterScheduleId: number;
  designChangeNo?: string;
  location?: string;
  elementOfWorks?: string;
  changeDetail?: string;
  changeReasons?: IUpsertProjectDesignModificationReasonOfChangeRequest[];
  riskCategory: string;
  nominatedChecker: IProjectTeam;
  nominatedCheckerCompany: IProjectCompanyOption;
  nominatedDesigner: IProjectTeam;
  nominatedDesignerCompany: IProjectCompanyOption;
  requiredApprovalDate: string;
  drawingsModified?: IUpsertProjectDesignModificationDrawingRequest[];
  designerComment?: string;
  iceComment?: string;
  reviewerComment?: string;
  discissions?: IUpsertProjectDesignModificationDiscussionRequest[];
  id: number;
  rowVersion?: string;
}

export interface IUpdateProjectDesignModificationStatusRequest extends IApiBaseProps {
  id: number;
}

export interface IApproveProjectDesignModificationRequest extends IUpdateProjectDesignModificationRequest {
  isRfc: boolean;
}

export interface IReassignCheckerForProjectDesignModificationRequest {
  id: number;
  projectId: number;
  rowVersion?: string;
  nominatedCheckerCompanyTeamId: number;
}

export interface IProjectDesignModificationOptionForDesignerTransmittalRequest extends IApiBaseProps {
  masterScheduleId: number;
}

export interface IProjectDesignModificationOptionForDesignerTransmittal {
  projectTransmittalDesignModificationRowVersion: string;
  projectTransmittalDesignModificationLinkId: number;
  id: number;
  projectMasterScheduleId: number;
  designChangeNo?: string;
  location?: string;
  elementOfWorks?: string;
  status?: string;
}

export interface IRiskCategoryRequest {
  id: number;
  category: string;
}

export interface ISearchDesignModificationDrawingRequest {
  ids: number[];
}
export interface IProjectDesignModificationSearchResult {
  id: number;
  projectId: number;
  designChangeNo?: string;
  riskCat?: string;
  location?: string;
  elementOfWorks?: string;
  changeDetail?: string;
  designerApproval?: boolean;
  iceApproval?: boolean;
  status?: string;
  isIceAction?: boolean;
  projectMasterSchedule: IMasterScheduleOption;
  actioner?: string;
  designer?: string;
  checker?: string;
  srp?: string;
  twc?: string;
  iceApprovedDate?: string;
}

export interface IProjectDesignModificationSearchFilter {
  projectId: NumberFilter;
  t1RefNo?: StringFilter;
  designChangeNo?: StringFilter;
  designPackage?: StringFilter;
  twc: OptionFilter;
  srp: OptionFilter;
  checker: OptionFilter;
  designer: OptionFilter;
  reasonForChange?: StringFilter;
  action?: StringFilter;
  elementOfWorks?: StringFilter;
  location?: StringFilter;
  riskCategory: OptionFilter;
}
export enum EDesignModificationSearchColumnKeys {
  T1_REF_NO = 'projectMasterSchedule.refNo',
  T3_NO = 'designChangeNo',
  DESIGN_PACKAGE = 'projectMasterSchedule.designPackage',
  TWC = 'twc',
  TWC_ISSUE = 'twcIssue',
  SRP = 'srp',
  CHECKER = 'checker',
  DESIGNER = 'designer',
  REASON_FOR_CHANGE = 'reasonForChange',
  LOCATION = 'location',
  ELEMENT_OF_WORKS = 'elementOfWorks',
  ACTIONER = 'actioner',
  RISK_CATEGORY = 'riskCat',
  ICE_DATE = 'iceApprovedDate',
}

export enum EDesignModificationSearchKey {
  designChangeNo = 'designChangeNo',
  actioner = 'actioner',
  riskCategory = 'riskCategory',
  t1RefNo = 't1RefNo',
  designPackage = 'designPackage',
  location = 'location',
  elementOfWorks = 'elementOfWorks',
  reasonForChange = 'reasonForChange',
  designer = 'designer',
  checker = 'checker',
  twc = 'twc',
  twcIssue = 'twcIssue',
  srp = 'srp',
}

export interface IDesignModificationFormState {
  [EDesignModificationFormKeys.LOCATION]: string | null;
  [EDesignModificationFormKeys.ELEMENT_OF_WORKS]: string | null;
  [EDesignModificationFormKeys.CHANGE_DETAIL]: string | null;
  [EDesignModificationFormKeys.PROPOSED_MODIFICATION_FILES]: IFileInfo[];
  [EDesignModificationFormKeys.REASONS_OF_CHANGE]: IReasonOfChangeBase[];
  [EDesignModificationFormKeys.REQUIRED_APPROVAL_DATE]: string | null;
  [EDesignModificationFormKeys.RISK_CATEGORY]: IRiskCategory | null;
  [EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY]: IProjectCompanyOption | null;
  [EDesignModificationFormKeys.NOMINATED_CHECKER]: IProjectTeamOption | null;
  [EDesignModificationFormKeys.NOMINATED_DESIGNER_COMPANY]: IProjectCompanyOption | null;
  [EDesignModificationFormKeys.NOMINATED_DESIGNER]: IProjectTeamOption | null;
  [EDesignModificationFormKeys.DRAWINGS_MODIFIED]: IOption<IProjectDrawingRegisterSearch>[];
  [EDesignModificationFormKeys.DESIGNER_COMMENT]: string | null;
  [EDesignModificationFormKeys.REVIEWER_COMMENT]: string | null;
  [EDesignModificationFormKeys.ICE_COMMENT]: string | null;
  [EDesignModificationFormKeys.DESIGNER_ATTACHMENTS]: IProjectFileBase[];
  [EDesignModificationFormKeys.DESIGNER_ATTACHMENTS_CALCULATION]: IProjectFileBase[];
}
export interface IDesignModificationReassignCheckerFormState {
  [EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY]: IProjectCompanyOption | null;
  [EDesignModificationFormKeys.NOMINATED_CHECKER]: IProjectTeamOption | null;
}
export enum EDesignModificationFormKeys {
  LOCATION = 'location',
  ELEMENT_OF_WORKS = 'elementOfWorks',
  CHANGE_DETAIL = 'changeDetail',
  PROPOSED_MODIFICATION_FILES = 'proposedModificationAttachments',
  REASONS_OF_CHANGE = 'changeReasons',
  REQUIRED_APPROVAL_DATE = 'requiredApprovalDate',
  RISK_CATEGORY = 'riskCategory',
  NOMINATED_CHECKER_COMPANY = 'nominatedCheckerCompany',
  NOMINATED_DESIGNER_COMPANY = 'nominatedDesignerCompany',
  NOMINATED_CHECKER = 'nominatedChecker',
  NOMINATED_DESIGNER = 'nominatedDesigner',
  CHECKER = 'checker',
  DRAWINGS_MODIFIED = 'drawingsModified',
  DESIGNER_COMMENT = 'designerComment',
  ICE_COMMENT = 'iceComment',
  REVIEWER_COMMENT = 'reviewerComment',
  DESIGNER_ATTACHMENTS = 'designerAttachments',
  DESIGNER_ATTACHMENTS_CALCULATION = 'designerAttachmentsCalculation',
  ICE_CERT = 'iceCert',
}

export enum EDesignModificationConfirmationActionType {
  create,
  update,
  cancel,
  requestEndorsement,
  issueToTwcAsSrp,
  issueToTwcAsDesigner,
  issueToDesigner,
  approveDesigner,
  approveReviewer,
  issueToChecker,
  approveChecker,
  approveAndCheckedAsLambeth,
  releaseForConstruction,
  createRevision,
}

export enum EDesignModificationFormSubmitType {
  create,
  createAndIssueAsSrp,
  createAndIssueAsTwc,
  createAndIssueAsDesigner,
  update,
  requestEndorsement,
  approve,
  approveAndCheckedAsLambeth,
  cancel,
}

export enum EDesignModificationStatus {
  initial = 'I',
  waitingToTWCApproval = 'T',
  waitingToDesignerApproval = 'D',
  waitingToICEApproval = 'E',
  waitingForReviewerApproval = 'RE',
  waitingToTWCIssue = 'W',
  waitingToUploadSignedCert = 'U',
  waitingTWCToRelease = 'R',
  completed = 'F',
  cancel = 'C',
  commented = 'CM',
  revised = 'RV',
  referToDesignerTransmittal = 'M',
}
export enum EDesignModificationAction {
  SRP = 'SRP',
  TWC = 'TWC',
  TWD = 'TWD',
  ICE = 'ICE',
  CANCELLED = 'Cancelled',
  DRAFT = 'Draft',
  NO_ACTION = '-',
}

export enum EDesignModificationIssueRole {
  designer = 'DESIGNER',
  srp = 'SRP',
  twc = 'TWC',
}

export enum EDesignModificationApprovalStatus {
  approved = 'A',
  aaitingForApproval = 'W',
  reject = 'R',
  notRequired = 'N',
}
